import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n/I18nProvider";
import { api, type NewsletterStatus } from "../lib/api";
import { CONSENT_EVENT, getConsent } from "../lib/analytics";
import { useCatalog } from "../lib/CatalogProvider";

const DISMISS_KEY = "pf-newsletter-dismissed";
/** After "Ahora no", the popup stays away this long. */
const DISMISS_DAYS = 21;
/** Time on the home page before it shows (and only once the cookie banner is answered). */
const DELAY_MS = 6000;

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

const dmy = (ymd: string) => ymd.split("-").reverse().join("/");

/**
 * Home page offer: subscribe to news and get a personal discount code. Without an account it leads to
 * sign-up (the news box comes ticked there); with one, a click subscribes and shows the code. Comes
 * back with ?novedades=1 (after signing in from it) even if dismissed.
 */
export function NewsletterPopup() {
  const { t, pick } = useI18n();
  const auth = useAuth();
  const { settings, catalog } = useCatalog();
  const location = useLocation();
  const forced = new URLSearchParams(location.search).get("novedades") === "1";
  const enabled = settings.newsletter_offer_enabled ? pick(settings.newsletter_offer_enabled) !== "off" : false;
  const pct = Number(settings.newsletter_offer_pct ? pick(settings.newsletter_offer_pct) : 5) || 5;
  const days = Number(settings.newsletter_offer_days ? pick(settings.newsletter_offer_days) : 30) || 30;
  const excludedSlugs = (() => {
    try {
      const v = JSON.parse(settings.newsletter_offer_excluded ? pick(settings.newsletter_offer_excluded) : "[]") as unknown;
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  })();
  const excludedNow = catalog.categories.filter((c) => excludedSlugs.includes(c.slug)).map((c) => pick(c.name));

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<NewsletterStatus | null>(null);
  const [state, setState] = useState<"idle" | "sending" | "error">("idle");
  const [copied, setCopied] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);

  // Signed-in: already subscribed (or not verified) means nothing to offer.
  useEffect(() => {
    if (!enabled || auth.loading || !auth.user) return;
    const ctrl = new AbortController();
    api
      .newsletter(ctrl.signal)
      .then(setStatus)
      .catch(() => setStatus(null));
    return () => ctrl.abort();
  }, [enabled, auth.loading, auth.user]);

  const eligible = enabled && !auth.loading && (!auth.user || (status !== null && !status.subscribed && status.offer.enabled));

  useEffect(() => {
    if (!eligible || open || (!forced && dismissedRecently())) return;
    let timer = 0;
    const arm = () => {
      if (getConsent() === null) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setOpen(true), forced ? 300 : DELAY_MS);
    };
    arm();
    window.addEventListener(CONSENT_EVENT, arm);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(CONSENT_EVENT, arm);
    };
  }, [eligible, open, forced]);

  useEffect(() => {
    if (!open) return;
    dialog.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  if (!open) return null;

  function close() {
    setOpen(false);
    if (!status?.coupon) {
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {
        // private mode: it may show again next visit
      }
    }
  }

  const subscribe = async () => {
    setState("sending");
    try {
      setStatus(await api.subscribeNewsletter());
      setState("idle");
    } catch {
      setState("error");
    }
  };

  const coupon = status?.subscribed ? status.coupon : null;
  const back = encodeURIComponent(`${location.pathname}?novedades=1`);

  return (
    <div className="evo-backdrop nl-backdrop" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="nl-modal" role="dialog" aria-modal="true" aria-labelledby="nl-title" tabIndex={-1} ref={dialog}>
        <button type="button" className="nl-close" aria-label={t("nav.close")} onClick={close}>
          ×
        </button>
        <div className="nl-ticket" aria-hidden="true">
          <span>{pct}%</span>
          <small>OFF</small>
        </div>
        {coupon ? (
          <>
            <h2 id="nl-title">{t("nl.doneTitle")}</h2>
            <p>{t("nl.doneText", { pct })}</p>
            <div className="nl-code">
              <code>{coupon.code}</code>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(coupon.code).then(() => setCopied(true));
                }}
              >
                {copied ? t("mkt.copied") : t("mkt.copy")}
              </button>
            </div>
            <p className="muted small">
              {t("nl.fine", { days: coupon.endsAt ? dmy(coupon.endsAt) : `${days}` })}
              {coupon.excluded.length > 0 && ` ${t("nl.excluded", { list: coupon.excluded.join(", ") })}`}
            </p>
            <Link to="/mercado" className="btn btn-primary btn-block" onClick={() => setOpen(false)}>
              {t("nl.shop")}
            </Link>
          </>
        ) : (
          <>
            <h2 id="nl-title">{t("nl.title", { pct })}</h2>
            <p>{t("nl.text", { pct })}</p>
            {auth.user ? (
              <button type="button" className="btn btn-primary btn-block" disabled={state === "sending"} onClick={subscribe}>
                {t("nl.subscribe", { pct })}
              </button>
            ) : (
              <>
                <Link to={`/registro?next=${back}`} className="btn btn-primary btn-block" onClick={() => setOpen(false)}>
                  {t("nl.register", { pct })}
                </Link>
                <p className="small">
                  {t("nl.haveAccount")}{" "}
                  <Link to={`/ingresar?next=${back}`} onClick={() => setOpen(false)}>
                    {t("auth.submitLogin")}
                  </Link>
                </p>
              </>
            )}
            {state === "error" && <p className="form-error">{t("error.generic")}</p>}
            {status?.subscribed && !status.coupon && <p className="muted small">{t("nl.pending")}</p>}
            <p className="muted small">
              {t("nl.terms", { days })}
              {excludedNow.length > 0 && ` ${t("nl.excluded", { list: excludedNow.join(", ") })}`}
            </p>
            <button type="button" className="btn btn-link btn-sm" onClick={close}>
              {t("nl.later")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
